import { Banner, Field, Select } from '@cloudflare/kumo';
import { useEffect, useState } from 'react';
import { funnelApi, type FunnelReport } from '../../services/funnelApi';
import { SkeletonCards } from '../ui/Skeleton';

const PERIODS = [
  { value: '30', label: 'últimos 30 dias' },
  { value: '90', label: 'últimos 90 dias' },
  { value: '365', label: 'últimos 12 meses' },
];

const brl = (v: number) => v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 });
const pct = (v: number | null) => (v === null ? '—' : `${Math.round(v * 100)}%`);
const monthLabel = (m: string | null) => (m ? new Date(`${m}-15T12:00:00`).toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' }) : 'Sem previsão de fechamento');

function Tile({ label, value, hint, testId }: { label: string; value: string; hint?: string; testId?: string }) {
  return (
    <div className="funil-metric" data-testid={testId}>
      <span className="text-xs">{label}</span>
      <h4 className="text-base font-semibold">{value}</h4>
      {hint ? <span className="text-xs text-kumo-subtle">{hint}</span> : null}
    </div>
  );
}

function Table({ caption, head, rows, empty }: { caption: string; head: string[]; rows: string[][]; empty: string }) {
  return (
    <section aria-label={caption} className="space-y-2">
      <h3 className="text-base font-semibold">{caption}</h3>
      {rows.length === 0 ? <div className="text-sm text-kumo-subtle">{empty}</div> : (
        <table className="w-full text-sm" style={{ borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ textAlign: 'left', color: 'var(--crm-text-muted, #6b7280)' }}>
              {head.map((h, i) => <th key={h} className="py-1" style={i ? { textAlign: 'right' } : undefined}>{h}</th>)}
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r[0]} style={{ borderTop: '1px solid var(--crm-border, #e5e7eb)' }}>
                {r.map((c, i) => <td key={i} className="py-1" style={i ? { textAlign: 'right', fontVariantNumeric: 'tabular-nums' } : undefined}>{c}</td>)}
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}

/** Relatórios do funil (#159): valor por etapa, previsão ponderada, conversão, motivos de perda e responsáveis */
export function FunnelReports({ pipelineId, projectId }: { pipelineId: string; projectId?: string | null }) {
  const [days, setDays] = useState(30);
  const [report, setReport] = useState<FunnelReport | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    setReport(null); setError(null);
    funnelApi.report({ pipelineId: pipelineId || null, projectId, days })
      .then((r) => { if (alive) setReport(r.report); })
      .catch((e: unknown) => { if (alive) setError(e instanceof Error ? e.message : 'Não foi possível carregar os relatórios'); });
    return () => { alive = false; };
  }, [pipelineId, projectId, days]);

  const period = PERIODS.find((p) => p.value === String(days)) ?? null;
  return (
    <div className="space-y-5" data-testid="funil-reports">
      <div className="funil-filters" role="region" aria-label="Período dos relatórios">
        <Field label="Período (fechamentos e criados)">
          <Select value={period?.value ?? null} items={PERIODS} onValueChange={(v: string | null) => { if (v) setDays(Number(v)); }} />
        </Field>
      </div>
      {error ? <Banner variant="error" description={<>{error}</>} /> : null}
      {!report && !error ? <SkeletonCards count={4} label="Carregando relatórios" /> : null}
      {report ? (
        <>
          <div className="funil-metrics" role="group" aria-label="Resumo do período">
            <Tile label="Previsão ponderada" value={brl(report.forecast.weightedAmount)} hint={`${brl(report.forecast.openAmount)} em ${report.forecast.openCount} negócios abertos`} testId="report-weighted" />
            <Tile label="Taxa de conversão" value={pct(report.conversion.winRate)} hint={`${report.conversion.won} ganhos · ${report.conversion.lost} perdidos`} testId="report-winrate" />
            <Tile label="Ganhos no período" value={brl(report.conversion.wonAmount)} hint={`${report.conversion.created} negócios criados`} />
            <Tile label="Ciclo médio até ganhar" value={report.conversion.avgCycleDays === null ? '—' : `${report.conversion.avgCycleDays.toLocaleString('pt-BR')} dias`} />
          </div>
          <Table caption="Valor por etapa (negócios em aberto)" head={['Etapa', 'Negócios', 'Valor', 'Ponderado', 'Prob.']}
            rows={report.stages.filter((s) => !s.isWon && !s.isLost).map((s) => [s.name, String(s.open.count), brl(s.open.amount), brl(s.open.weighted), `${s.probability}%`])} empty="Sem etapas." />
          <Table caption="Previsão de fechamento por mês" head={['Mês', 'Negócios', 'Valor', 'Ponderado']}
            rows={report.forecast.byMonth.map((m) => [monthLabel(m.month), String(m.count), brl(m.amount), brl(m.weighted)])} empty="Nenhum negócio em aberto." />
          <Table caption="Motivos de perda" head={['Motivo', 'Negócios', 'Valor']}
            rows={report.lostReasons.map((r) => [r.reason, String(r.count), brl(r.amount)])} empty="Nenhuma perda no período." />
          <Table caption="Por responsável" head={['Responsável', 'Em aberto', 'Ganhos', 'Valor ganho', 'Conversão']}
            rows={report.owners.map((o) => [o.name, `${o.open.count} · ${brl(o.open.amount)}`, String(o.won.count), brl(o.won.amount), pct(o.winRate)])} empty="Nenhum negócio." />
        </>
      ) : null}
    </div>
  );
}
